export interface LoginRequest {
  email: string;
  password: string;
}

export interface WorkshopInfo {
  workshopId: string;
  name: string;
}

export interface LoginResponse {
  token: string;
  email: string;
  role: string;
  userId: string;
  firstName: string;
  lastName: string;
  workshops?: WorkshopInfo[]; // Array of workshops user belongs to
  isFirstLogin?: boolean;
  user?: User; // Opsiyonel, eski format desteği için
}

export enum OrderStatus {
  Atanmadi = 1, // Atanmadı
  Islemde = 2, // İşlemde
  IptalEdildi = 3, // İptal Edildi
  Tamamlandi = 4, // Tamamlandı
}

export enum OrderUnit {
  Adet = 1, // Database ID: 1
  Metre = 2, // Database ID: 2
  Takim = 3, // Database ID: 3 (Takım birimi)
  Pano = 7, // Database ID: 7 (Pano birimi)
}

// Maliyet hesaplama tipleri
export enum CalculationType {
  Simple = 0, // Basit hesaplama - Quantity × UnitPrice
  TwoDimensional = 1, // İki boyutlu - Quantity × Quantity2 × UnitPrice
  PieceFitting = 2, // Varak/Sticker - (MaterialWidth/En) × (MaterialHeight/Boy) = adet/metre
  MeterBased = 3, // Metre bazlı hesaplama
  AreaBased = 4, // Alan bazlı (m²) hesaplama
  PaintBased = 5, // Boya hesaplama - Litre × Kat Sayısı (Çeşit Sayısı yalnız bilgi amaçlı)
  CustomCost = 6, // Özel Maliyet - TotalCost direkt girilir (Fason üretim, sabit maliyetler)
}

export interface ExchangeRate {
  exchangeRateId: string;
  currencyCode: string;
  currencyName: string;
  forexBuying: number;
  forexSelling: number;
  banknoteBuying: number;
  banknoteSelling: number;
  rateDate: string;
  unit: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  isActive: boolean;
}

export interface Currency {
  code: string;
  name: string;
}

export interface InvoiceType {
  invoiceTypeId: number; // Backend'de int olarak tanımlı (1, 2, 3)
  name: string;
  description?: string;
}

// Firma Tipi Enum
export enum FirmType {
  TuzelKisi = 0, // Tüzel Kişi (Şirket)
  GercekKisi = 1, // Gerçek Kişi (Şahıs)
}

export interface City {
  cityId: number;
  cityName: string; // Backend'de CityName olarak geliyor
  name?: string; // Compatibility için
  code?: string;
}

export interface District {
  districtId: number;
  districtName: string; // Backend'de DistrictName olarak geliyor
  name?: string; // Compatibility için
  cityId: number;
  city?: City;
}

export interface TaxOffice {
  taxOfficeId: number;
  name: string;
  cityId: number;
  city?: City;
}

export interface Firm {
  firmId: string;
  firmCode: string;
  firmName: string;
  firmType?: FirmType;
  description?: string;
  address?: string;

  // İlişkisel alanlar
  cityId?: number;
  city?: City;
  districtId?: number;
  district?: District;
  taxOfficeId?: number;
  taxOffice?: TaxOffice;

  countryCode?: string;
  countryName?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  taxNumber?: string;
  tckn?: string;
  firstName?: string;
  lastName?: string;
  invoiceTypeId?: number; // Backend'de int? nullable olarak tanımlı
  invoiceType?: InvoiceType;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  isActive: boolean;
}
export interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  workshops?: WorkshopInfo[]; // Array of workshops user belongs to
  isActive: boolean;
}

export interface Model {
  modelId: string;
  modelCode: string;
  modelName: string;
  firmId?: string; // Firma ID'si
  patternCode?: string; // Desen kodu
  description?: string;
  category?: string;
  season?: string;
  color?: string;
  size?: string;
  fabric?: string;
  estimatedPrice?: number;
  price?: number;
  priceCurrency?: string;
  estimatedProductionTime?: number;
  imageUrl?: string;
  productionStockId?: string; // Üretim stoğu (Mamül) — sipariş tamamlanınca BOM tüketimi için
}

export interface ModelPriceHistoryItem {
  orderId: string;
  acceptanceDate: string;
  price: number;
  priceCurrency: string;
  quantity: number;
  orderUnitName: string;
  firmName: string;
  firmId: string;
}

export interface ModelPriceHistory {
  modelId: string;
  modelCode: string;
  modelName: string;
  currentPrice?: number;
  currentPriceCurrency?: string;
  priceHistory: ModelPriceHistoryItem[];
}

export interface Workshop {
  workshopId: string;
  name: string;
  description?: string;
  location?: string;
  contactPerson?: string;
  phone?: string;
  displayOrder?: number;
}

export interface Operator {
  operatorId: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  specialization?: string;
  workshopId?: string;
  workshop?: Workshop;
}

export interface Technic {
  technicId: string;
  name: string;
  description?: string;
  category?: string;
  workshopId?: string;
  workshop?: Workshop;
}

export interface OrderImage {
  orderImageId: string;
  orderId: string;
  imageUrl: string;
  description?: string;
  displayOrder: number;
}

export interface OrderTechnic {
  orderTechnicId?: string;
  orderId: string;
  technicId: string;
  technic?: Technic;
}

export interface Order {
  orderId: string;
  acceptanceDate: string; // Backend: AcceptanceDate
  modelistUserId?: string; // Modelist assignment for digital/sticket orders
  modelistUser?: User; // Navigation property for modelist user
  completionDate?: string; // Backend: CompletionDate (nullable)
  firmId: string; // Backend: FirmId
  firm?: Firm; // Navigation property
  modelId: string; // Backend: ModelId
  model?: Model;
  quantity: number; // Backend: Quantity
  unit?: OrderUnit; // Backend: Unit (enum: Adet=0, Metre=1, Takim=2)
  pieceCount?: number; // Backend: PieceCount (Takım seçiliyse takımda kaç parça var)
  width?: number; // Backend: Width (En) - opsiyonel
  height?: number; // Backend: Height (Boy) - opsiyonel
  price?: number; // Backend: Price (nullable decimal)
  priceCurrency?: string; // Backend: PriceCurrency (döviz kodu: USD, EUR, TRY, vb.)
  currency?: string; // Eski alan - geriye dönük uyumluluk için
  workshopId?: string; // Backend: WorkshopId (nullable)
  workshop?: Workshop;
  workshopDuration?: string; // Kanban endpoint'inden gelen atölyede geçen süre
  operatorId?: string; // Backend: OperatorId (nullable)
  operator?: Operator;
  priority?: string; // Backend: Priority (nullable, max 50)
  deadline?: string; // Backend: Deadline (nullable DateTime)
  note?: string; // Backend: Note (nullable, max 500)
  invoice?: string; // Backend: Invoice (nullable, max 100)
  invoiceNumber?: string; // Backend: InvoiceNumber (nullable, max 50)
  status?: OrderStatus; // Backend: Status (enum)
  isSample?: boolean; // Backend: IsSample - Numune sipariş mi?
  qrCodeUrl?: string; // Backend: QrCodeUrl (nullable)
  qrCode?: string; // Eski alan - geriye dönük uyumluluk için
  images?: OrderImage[];
  orderTechnics?: OrderTechnic[];
  // AuditableEntity fields
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  isActive: boolean;
}

// Cost Management Types
export interface CostCategory {
  costCategoryId: string;
  category: string; // Backend expects 'category' field
  categoryName: string; // Keep for frontend compatibility
  description?: string;
  displayOrder?: number;
  isActive: boolean;
}

export interface CostSubCategory {
  costSubCategoryId: string; // GUID
  costCategoryId: string; // GUID
  subCategory: string; // Backend expects 'subCategory'
  subCategoryName: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  costCategory?: CostCategory;
}

export interface CostItem {
  costItemId: string; // GUID
  costCategoryId: string; // GUID
  costUnitId?: string; // GUID - Yeni format
  costUnitId2?: string; // GUID - İkinci birim (opsiyonel)
  costUnitId3?: string; // GUID - Üçüncü birim (opsiyonel, sadece referans)
  itemName: string;
  description?: string;
  unit?: string; // String format (unitCode) - Opsiyonel, hesaplama tipine göre belirlenir
  unitName?: string; // Birim adı (Metre, Kilogram, Litre vb.) - Backend'den geliyor
  unit2?: string; // İkinci birim adı (opsiyonel, çoklu boyut için) - DEPRECATED: costUnit2.unitName kullan
  unitPrice: number;
  currency: string;
  supplier?: string;
  wastagePercentage?: number; // Fire yüzdesi (0-100)
  minimumOrderQuantity?: number;
  discountPercentage?: number;
  lastPriceUpdate: string;
  isActive: boolean;
  // Hesaplama tipi ve ilgili alanlar
  calculationType?: CalculationType; // Hesaplama tipi (Simple, PieceFitting vb.)
  materialWidth?: number; // Malzeme eni (Varak: 150, Sticker: 59)
  materialHeight?: number; // Malzeme boyu per metre (100)
  quantity1Label?: string; // 1. miktar başlığı ("En", "Miktar", "Kişi")
  quantity2Label?: string; // 2. miktar başlığı ("Boy", "Süre", "Saat")
  quantity3Label?: string; // 3. miktar başlığı ("Sipariş Adedi", "Adet")
  // Navigation properties
  costCategory?: CostCategory;
}

export interface ModelCost {
  modelCostId: string;
  modelId: string;
  modelName?: string; // Backend'den flat DTO
  modelCode?: string; // Backend'den flat DTO
  orderId?: string; // Sipariş ID'si (opsiyonel - genel model maliyetleri için null olabilir)
  orderAcceptanceDate?: string; // Backend'den flat DTO
  orderQuantity?: number; // Sipariş adedi (Backend'den flat DTO)
  firmId?: string; // Firma ID'si (flat DTO)
  firmName?: string; // Firma adı (flat DTO)
  oldWorkshopId?: string; // Atölye ID'si (flat DTO)
  oldWorkshopName?: string; // Atölye adı (flat DTO)
  costItemId: string;
  costItemName?: string; // Backend'den flat DTO
  costCategoryName?: string; // Backend'den flat DTO
  quantity: number;
  unit?: string;
  quantity2?: number; // İkinci miktar (boy, kişi sayısı, süre vb.)
  unit2?: string; // İkinci birim adı
  quantity3?: number; // Üçüncü miktar (opsiyonel, referans)
  unit3?: string; // Üçüncü birim adı (opsiyonel, referans)
  quantity1Label?: string; // 1. miktar başlığı ("En", "Miktar", "Kişi", vb.)
  quantity2Label?: string; // 2. miktar başlığı ("Boy", "Çeşit Sayısı", vb.)
  costUnitId2?: string; // İkinci birim referansı
  costUnitId3?: string; // Üçüncü birim referansı (opsiyonel, referans)
  calculationType?: CalculationType; // Hesaplama tipi (Simple, PieceFitting, CustomCost vb.)
  unitPrice?: number;
  totalCost?: number;
  currency?: string;
  actualQuantityNeeded?: number;
  wastagePercentage?: number; // Fire yüzdesi (0-100)
  usage?: string;
  notes?: string; // Backend'den flat DTO
  priority?: number;
  isActive: boolean;
  usdRate?: number; // USD kuru (maliyet kaydedildiği andaki kur)
  eurRate?: number; // EUR kuru (maliyet kaydedildiği andaki kur)
  gbpRate?: number; // GBP kuru (maliyet kaydedildiği andaki kur)
  exchangeRateDate?: string; // Kur tarihi
  createdAt?: string; // Kayıt oluşturulma tarihi
  // Settings-based pricing calculations
  overheadCostRate?: number; // Genel gider oranı (%)
  profitMargin?: number; // Kar marjı (%)
  costWithOverhead?: number; // Genel gider eklenmiş maliyet
  finalCostWithProfit?: number; // Kar marjı eklenmiş nihai maliyet
  // PaintBased calculation type için özel alanlar
  layerCount?: number; // Kat sayısı (PaintBased için)
  applicationMethod?: string; // Uygulama yöntemi (PaintBased için)
  // AreaBased calculation type için özel alan
  coverageArea?: number; // Kaplama alanı (AreaBased için)
  model?: Model; // Geriye dönük uyumluluk için
  order?: Order; // Geriye dönük uyumluluk için
  costItem?: CostItem; // Geriye dönük uyumluluk için
}

export interface ModelCostSummary {
  modelId: string;
  modelName: string;
  totalItems: number;
  totalCost: number;
  currency: string;
  costsByCategory: {
    categoryName: string;
    itemCount: number;
    totalCost: number;
    items: {
      itemName: string;
      quantity: number;
      unit: string;
      unitPrice: number;
      totalCost: number;
      usage?: string;
    }[];
  }[];
}

export interface WorkshopCostItem {
  workshopCostItemId: string;
  workshopId: string;
  workshopName?: string;
  costItemId: string;
  costItemName?: string;
  costItemDescription?: string;
  categoryName?: string;
  costUnitId?: string;
  unitName?: string;
  unitCode?: string;
  costUnitId2?: string;
  unitName2?: string;
  unitCode2?: string;
  costUnitId3?: string;
  unitName3?: string;
  unitCode3?: string;
  // Hesaplama tipi ve özel miktar etiketleri (CostItem'dan gelen)
  calculationType?: CalculationType;
  materialWidth?: number; // Malzeme eni (Varak: 150, Sticker: 59)
  materialHeight?: number; // Malzeme boyu per metre (100)
  quantity1Label?: string;
  quantity2Label?: string;
  quantity3Label?: string;
  standardPrice?: number;
  standardCurrency?: string;
  workshopSpecificPrice?: number;
  workshopCurrency?: string;
  workshopDiscountPercentage?: number;
  effectivePrice: number;
  effectiveCurrency?: string;
  priority?: number;
  isPreferred: boolean;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  workshop?: Workshop;
  costItem?: CostItem; // Geriye dönük uyumluluk için
}

export interface OrderWorkshopCost {
  orderWorkshopCostId: string;
  orderId: string;
  workshopId: string;
  costItemId: string;
  quantityUsed: number;
  quantity2?: number; // İkinci boyut (opsiyonel)
  quantity3?: number; // Üçüncü boyut (opsiyonel, referans)
  unit?: string; // Birim (required by backend ModelCost)
  unit2?: string; // İkinci birim (opsiyonel)
  unit3?: string; // Üçüncü birim (opsiyonel, referans)
  costUnitId3?: string; // Üçüncü birim ID (opsiyonel, referans)
  wastagePercentage?: number; // Fire yüzdesi (0-100)
  actualPrice: number;
  currency: string;
  totalCost: number;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  order?: Order;
  workshop?: Workshop;
  costItem?: CostItem;
}

export interface Settings {
  settingsId: string;
  settingsName: string;
  profitMargin: number; // Kar marjı (%)
  overheadCostRate: number; // Genel gider oranı (%)
  showStockManagement: boolean; // Stok yönetim modülü görünürlüğü
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Pagination types
export interface PaginatedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// ============================================
// STOK YÖNETİM SİSTEMİ TİPLERİ
// ============================================

// Stok Tipi Enum
export enum StockType {
  Hammadde = 1, // Ham madde - kumaş, iplik, boya vb.
  YariMamul = 2, // Yarı mamül - kesilmiş kumaş, boyanmış iplik vb.
  Mamul = 3, // Mamül - bitmiş ürün, dikim için hazır parça vb.
}

// Stok Hareket Tipi Enum
export enum StockMovementType {
  Giris = 1, // Giriş
  Cikis = 2, // Çıkış
  Transfer = 3, // Transfer
  Sayim = 4, // Sayım Düzeltmesi
  Iade = 5, // İade
  Fire = 6, // Fire/Zayi
}

// Stok Kategorisi
export interface StockCategory {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Stok
export interface Stock {
  id: string;
  productCode: string;
  productName: string;
  description?: string;
  categoryId: string;
  category?: StockCategory;
  stockType?: StockType;
  unit: string;
  currentStock: number;
  minimumStock?: number;
  maximumStock?: number;
  unitPrice?: number;
  currency?: string;
  warehouseLocation?: string;
  supplier?: string;
  color?: string;
  size?: string;
  barcode?: string;
  isActive: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Stok Hareketi
export interface StockMovement {
  id: string;
  stockId: string;
  stock?: Stock;
  movementType: StockMovementType;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
  currency?: string;
  referenceNo?: string;
  referenceType?: string;
  description?: string;
  movementDate: string;
  movedBy?: string;
  fromWarehouse?: string;
  toWarehouse?: string;
  relatedOrderId?: number;
  relatedOrder?: Order;
  isActive: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Stok Reçetesi (BOM - Bill of Materials)
export interface StockRecipe {
  id: string;
  parentStockId: string;
  parentStock?: Stock;
  childStockId: string;
  childStock?: Stock;
  requiredQuantity: number;
  wastagePercentage?: number;
  description?: string;
  sortOrder?: number;
  isActive: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Stok Birimi
export interface StockUnit {
  id: string;
  unitName: string;
  abbreviation?: string;
  sortOrder?: number;
  isActive: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Üretim Tüketim İsteği
export interface ProductionConsumeRequest {
  stockId: string;
  quantity: number;
  referenceNo?: string;
  movedBy?: string;
  relatedOrderId?: string;
}

// Stok Oluşturma/Güncelleme İsteği
export interface StockRequest {
  id?: string;
  productCode: string;
  productName: string;
  description?: string;
  categoryId: string;
  stockType?: StockType;
  unit: string;
  currentStock: number;
  minimumStock?: number;
  maximumStock?: number;
  unitPrice?: number;
  currency?: string;
  warehouseLocation?: string;
  supplier?: string;
  color?: string;
  size?: string;
  barcode?: string;
  isActive?: boolean;
}

// Stok Hareketi Oluşturma/Güncelleme İsteği
export interface StockMovementRequest {
  id?: string;
  stockId: string;
  movementType: StockMovementType;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
  currency?: string;
  referenceNo?: string;
  referenceType?: string;
  description?: string;
  movementDate?: string;
  movedBy?: string;
  fromWarehouse?: string;
  toWarehouse?: string;
  relatedOrderId?: number;
  isActive?: boolean;
}

// Stok Reçetesi Oluşturma/Güncelleme İsteği
export interface StockRecipeRequest {
  id?: string;
  parentStockId: string;
  childStockId: string;
  requiredQuantity: number;
  wastagePercentage?: number;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}

// Tam BOM Ağacı (Rekürsif)
export interface FullBOMItem {
  stock: Stock;
  requiredQuantity: number;
  wastagePercentage?: number;
  children?: FullBOMItem[];
}

// Report types
export interface OrderCostItemDto {
  costItemName: string;
  quantity: number;
  unitPrice: number;
  totalCost: number;
  currency: string;
}

export interface OrderWithCostReportDto {
  orderId: string;
  orderCode: string;
  acceptanceDate: string;
  completionDate?: string;
  firmId: string;
  firmName: string;
  modelId: string;
  modelCode: string;
  modelName: string;
  quantity: number;
  unit: string;
  price?: number;
  priceCurrency: string;
  technics?: string[]; // Eski alan (backward compatibility)
  technicNames?: string[]; // Yeni alan
  paintCount?: number; // Boya adedi (PaintBased kalemlerin çeşit sayısı toplamı)
  costItems: OrderCostItemDto[];
  totalCostTRY: number;
  totalCostUSD: number;
  totalCostEUR: number;
  costWithOverhead: number;
  finalCostWithProfit: number;
}

export interface ReportSummaryDto {
  totalQuantity: number;
  totalAmount: number;
  totalCost: number;
  totalCostWithOverhead: number;
  totalFinalCost: number;
  profitLoss: number;
  profitLossPercentage: number;
}

// Aylık Adet Raporu
export interface MonthlyQuantityDto {
  year: number;
  month: number;
  monthName: string;
  orderCount: number;
  totalQuantity: number;
  totalPieceCount: number;
}

// Aylık Ciro Raporu
export interface MonthlyRevenueDto {
  year: number;
  month: number;
  monthName: string;
  orderCount: number;
  totalQuantity: number;
  revenueTRY: number;
  revenueUSD: number;
  revenueEUR: number;
  totalRevenueTRY: number;
}

// Müşteri Bazlı Ciro ve Adet Raporu
export interface CustomerRevenueDto {
  firmId: string;
  firmName: string;
  firmCode: string;
  orderCount: number;
  totalQuantity: number;
  totalPieceCount: number;
  revenueTRY: number;
  revenueUSD: number;
  revenueEUR: number;
  totalRevenueTRY: number;
}

// Aylık Kâr Raporu
export interface MonthlyProfitDto {
  year: number;
  month: number;
  monthName: string;
  orderCount: number;
  totalRevenueTRY: number;
  totalCostTRY: number;
  totalCostWithOverhead: number;
  grossProfit: number;
  netProfit: number;
  profitMarginPercent: number;
}

// Dynamic Report types
export interface DynamicReportColumn {
  fieldName: string;
  displayName: string;
  dataType: string;
  isFilterable: boolean;
  isSortable: boolean;
  isGroupable: boolean;
  isAggregatable: boolean;
  hasValueList?: boolean;
}

export interface ReportColumnMeta {
  label: string;
  dataType: string;
}

export interface DynamicReportDataSource {
  dataSourceName: string;
  displayName: string;
  description: string;
  availableColumns: DynamicReportColumn[];
}

export interface DynamicReportFilter {
  field: string;
  operator: string;
  value: string;
  value2?: string;
}

export interface DynamicReportAggregation {
  field: string;
  function: string;
  alias: string;
}

export interface DynamicReportRequest {
  dataSource: string;
  selectedColumns: string[];
  filters: DynamicReportFilter[];
  groupByColumns: string[] | null;
  aggregations: DynamicReportAggregation[] | null;
  sortBy?: string;
  sortDescending?: boolean;
  pageNumber: number;
  pageSize: number;
  targetCurrency?: string; // TRY | USD | EUR | GBP
  rateMode?: string; // historical | current
}

export interface DynamicReportResponse {
  columns: string[];
  columnMeta: Record<string, ReportColumnMeta>;
  rows: Record<string, any>[];
  aggregations: any;
  targetCurrency: string;
  rateMode: string;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// ===== Birleşik Pivot Rapor =====

export interface PivotFieldInfo {
  fieldName: string;
  displayName: string;
  dataType: string; // string | datetime | decimal | int | bool | currency
  category: string; // Tarih, Sipariş, Firma, Model, Atölye, Teknik, Maliyet, Sayısal, Ölçüler
  role: "dimension" | "measure" | "filter";
  grain?: "order" | "cost";
  hasValueList: boolean;
}

export interface PivotRequest {
  rows: string[];
  columns: string[];
  measures: DynamicReportAggregation[];
  filters: DynamicReportFilter[];
  targetCurrency?: string;
  rateMode?: string;
}

export interface PivotMeasureMeta {
  alias: string;
  label: string;
  dataType: string; // decimal | currency | int
}

export interface PivotResponse {
  rowFields: string[];
  columnFields: string[];
  fieldLabels: Record<string, string>;
  measures: PivotMeasureMeta[];
  rowHeaders: string[][];
  columnKeys: string[];
  columnHeaders: string[][]; // her sütun anahtarının alan değerleri
  columnGroups: string[]; // 2 sütun alanında 1. seviye daraltma grupları
  cells: (number | null)[][][]; // [satır][sütun][ölçü]
  rowTotals: (number | null)[][];
  columnTotals: (number | null)[][];
  grandTotals: (number | null)[];
  groupCells: (number | null)[][][]; // [satır][grup][ölçü] — daraltılmış ara toplamlar
  groupTotals: (number | null)[][]; // [grup][ölçü]
  recordCount: number;
  orderCount: number;
  targetCurrency: string;
  rateMode: string;
}

export interface ReportTemplate {
  reportTemplateId: string;
  name: string;
  description?: string;
  dataSource: string;
  configJson: string;
  isPublic: boolean;
  createdByUserId: string;
  createdByUserName?: string;
  createdAt: string;
  updatedAt?: string;
}

// ========================================
// External API Types (Softana Ticket API)
// ========================================

// JWT Token Authentication
export interface ExternalAuthTokenRequest {
  customerId: string;
  apiKey: string;
  externalUser: string;
  userName?: string;
  email?: string;
}

export interface ExternalAuthTokenResponse {
  token: string;
  expiresAt: string;
  userId: string;
  customerId: string;
  userName: string;
  email: string;
  role: string;
}

// Form Field Types
export type ExternalFormFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "select"
  | "checkbox"
  | "radio"
  | "file"
  | "photo"
  | "video";

export interface ExternalFormField {
  id: string;
  label: string;
  fieldType: ExternalFormFieldType;
  isRequired: boolean;
  sortOrder: number;
  placeholder?: string | null;
  options: string[];
  maxFileSizeMb?: number | null;
  allowedExtensions?: string | null;
  sectionId?: string | null;
  width?: "full" | "half" | null;
  column?: "left" | "right" | null;
}

export interface ExternalFormSection {
  id: string;
  title: string;
  description?: string | null;
  sortOrder: number;
  fields: ExternalFormField[];
}

export interface ExternalForm {
  id: string;
  name: string;
  description: string;
  fields: ExternalFormField[];
  sections: ExternalFormSection[];
}

// Form Submission
export interface ExternalFormSubmitResponse {
  id: string;
  statusCode: string;
  statusName: string;
  submittedAt: string;
}

// Submission List
export interface ExternalSubmissionListItem {
  id: string;
  formId: string;
  formName: string;
  statusCode: string;
  statusName: string;
  submittedAt: string;
  updatedAt: string;
}

// Submission Detail
export interface ExternalSubmissionValue {
  fieldId: string;
  label: string;
  fieldType: string;
  value: string;
}

export interface ExternalSubmissionFile {
  fieldId: string;
  label: string;
  originalFileName: string;
  url: string;
  sizeBytes: number;
  isImage: boolean;
}

export interface ExternalSubmissionDetail {
  id: string;
  formId: string;
  formName: string;
  statusCode: string;
  statusName: string;
  submittedAt: string;
  updatedAt: string;
  values: ExternalSubmissionValue[];
  files: ExternalSubmissionFile[];
}
